import { z } from 'zod';
import { INTEGRATION_RESOURCES } from './integration-resources';
import { digestPayloadSchema } from './notification-digests';

// Bump when a payload shape changes incompatibly. Emitted rows keep the version
// they were written with, so consumers can branch on it forever.
export const INTEGRATION_EVENT_API_VERSION = '2026-09-05';

export type IntegrationEventVisibility = 'internal' | 'public';

export interface IntegrationEventDef {
	label: string;
	description: string;
	visibility: IntegrationEventVisibility;
	schema: z.ZodType;
	// Delivered only to the one rule named on the outbox row. No rule can
	// subscribe to it, and the dispatcher never matches it against rules.
	addressedOnly?: boolean;
	// Raised as the event itself comes into being, so a rule scoped to that event
	// cannot exist yet to hear it. Only an organization-wide rule ever matches.
	precedesEvent?: boolean;
}

const eventCreatedPayload = z.object({
	organizationId: z.string(),
	organizationName: z.string(),
	eventId: z.string(),
	eventName: z.string(),
	startDate: z.string(),
	endDate: z.string().nullable(),
	timezone: z.string().nullable(),
	venueName: z.string().nullable(),
	venueAddress: z.string().nullable(),
	createdByUserId: z.string(),
	createdByName: z.string().nullable(),
	createdByEmail: z.string()
});

const eventEndedPayload = z.object({
	organizationId: z.string(),
	organizationName: z.string(),
	eventId: z.string(),
	eventName: z.string(),
	endDate: z.string(),
	timezone: z.string().nullable(),
	attendeeCount: z.number().int()
});

const userFirstLoginPayload = z.object({
	userId: z.string(),
	name: z.string().nullable(),
	email: z.string(),
	organizationId: z.string().nullable(),
	organizationName: z.string().nullable(),
	loggedInAt: z.string()
});

const paymentsOnboardingStartedPayload = z.object({
	organizationId: z.string(),
	organizationName: z.string(),
	stripeAccountId: z.string(),
	testMode: z.boolean(),
	startedByUserId: z.string(),
	startedByEmail: z.string()
});

const paymentsOnboardingCompletedPayload = z.object({
	organizationId: z.string(),
	organizationName: z.string(),
	stripeAccountId: z.string(),
	chargesEnabled: z.boolean(),
	payoutsEnabled: z.boolean(),
	detailsSubmitted: z.boolean()
});

const eventGoalReachedPayload = z.object({
	organizationId: z.string(),
	organizationName: z.string(),
	eventId: z.string(),
	eventName: z.string(),
	eventGoalId: z.string(),
	metric: z.string(),
	target: z.number().int(),
	actual: z.number(),
	achievedAt: z.string()
});

export const INTEGRATION_EVENTS = {
	'event.created': {
		label: 'Event Created',
		description: 'Someone created an event',
		visibility: 'public',
		precedesEvent: true,
		schema: eventCreatedPayload
	},
	'event.ended': {
		label: 'Event Ended',
		description: "An event's end date has passed",
		visibility: 'public',
		schema: eventEndedPayload
	},
	'user.first_login': {
		label: 'First Login',
		description: 'An admin user signed in for the first time',
		visibility: 'internal',
		schema: userFirstLoginPayload
	},
	'payments.onboarding_started': {
		label: 'Stripe Onboarding Started',
		description: 'An organization opened a Stripe Connect onboarding link',
		visibility: 'internal',
		schema: paymentsOnboardingStartedPayload
	},
	'payments.onboarding_completed': {
		label: 'Stripe Onboarding Completed',
		description: 'An organization finished Stripe Connect onboarding and can take charges',
		visibility: 'internal',
		schema: paymentsOnboardingCompletedPayload
	},
	'event.goal_reached': {
		label: 'Goal Reached',
		description: 'An event goal hit its target',
		visibility: 'public',
		schema: eventGoalReachedPayload
	},
	// Payload is the record plane's read shape: raised by mirroring the record
	// event rather than by an emit site of its own. order.completed is
	// deliberately not mirrored: every paid or $0 registration completes an order
	// at the same moment, so planners would hear about each signup twice.
	'registration.completed': {
		label: 'Registration Completed',
		description: 'Someone finished registering for an event',
		visibility: 'public',
		schema: INTEGRATION_RESOURCES.registration.readSchema
	},
	'registration.first_completed': {
		label: 'First Registration',
		description: "An event's first registration completed",
		visibility: 'public',
		schema: INTEGRATION_RESOURCES.registration.readSchema
	},
	'notification.digest': {
		label: 'Digest',
		description: 'A scheduled summary addressed to one rule',
		visibility: 'internal',
		addressedOnly: true,
		schema: digestPayloadSchema
	}
} as const satisfies Record<string, IntegrationEventDef>;

export type IntegrationEventType = keyof typeof INTEGRATION_EVENTS;

export type EventCreatedPayload = z.infer<typeof eventCreatedPayload>;
export type EventEndedPayload = z.infer<typeof eventEndedPayload>;
export type UserFirstLoginPayload = z.infer<typeof userFirstLoginPayload>;
export type PaymentsOnboardingStartedPayload = z.infer<typeof paymentsOnboardingStartedPayload>;
export type PaymentsOnboardingCompletedPayload = z.infer<typeof paymentsOnboardingCompletedPayload>;
export type EventGoalReachedPayload = z.infer<typeof eventGoalReachedPayload>;

export type IntegrationEventPayload<T extends IntegrationEventType> = z.infer<
	(typeof INTEGRATION_EVENTS)[T]['schema']
>;

export function isIntegrationEventType(value: string): value is IntegrationEventType {
	return Object.prototype.hasOwnProperty.call(INTEGRATION_EVENTS, value);
}

export function integrationEventTypesByVisibility(
	visibility: IntegrationEventVisibility
): IntegrationEventType[] {
	return (Object.keys(INTEGRATION_EVENTS) as IntegrationEventType[]).filter(
		(type) => INTEGRATION_EVENTS[type].visibility === visibility
	);
}

export interface IntegrationEventEnvelope {
	id: string;
	type: string;
	occurred_at: string;
	api_version: string;
	data: unknown;
}

// The wire shape every external consumer sees. It exists from day one, before
// anything serialises it, because the partner API's list responses must be
// byte-identical in shape to this `data` — settling the contract once is far
// cheaper than reconciling two shapes after Zapier is reading one of them.
// snake_case is deliberate: this is an external contract, not internal code.
export function buildEventEnvelope(row: {
	integrationEventId: string;
	type: string;
	apiVersion: string;
	createdAt: string;
	payload: unknown;
}): IntegrationEventEnvelope {
	return {
		id: `evt_${row.integrationEventId}`,
		type: row.type,
		// Stored timestamps are mode-string UTC without a zone marker; consumers
		// need an unambiguous instant.
		occurred_at: new Date(`${row.createdAt.replace(' ', 'T')}Z`).toISOString(),
		api_version: row.apiVersion,
		data: row.payload
	};
}
